import type { ReactNode } from "react";
import { waveformBars } from "@/lib/waveform-bars";
import { cn } from "@web-shared/lib/utils";
import { usePlayer, useTrackProgress, type PlayerTrack } from "../player";

const BARS = 96;

/** A mark on the waveform — a timed comment's avatar. */
export interface WaveformMarker {
  key: string;
  /** 0–1 along the track. */
  at: number;
  title: string;
  node: ReactNode;
}

/**
 * SoundCloud's waveform: the seed's shape, filled in as it plays, and a click
 * anywhere jumps there (starting the seed there if it was not playing).
 */
export function Waveform({
  track,
  waveform,
  queue,
  markers = [],
  className,
}: {
  track: PlayerTrack | null;
  waveform: string | null | undefined;
  queue?: PlayerTrack[];
  markers?: WaveformMarker[];
  className?: string;
}) {
  const player = usePlayer();
  const progress = useTrackProgress(track?.id ?? "");
  const bars = waveformBars(waveform, BARS);
  const played = progress ?? 0;

  return (
    <div className={cn("relative", className)}>
      <button
        type="button"
        disabled={!track}
        aria-label={track ? `Seek in ${track.title}` : "No audio"}
        className="block h-full w-full cursor-pointer disabled:cursor-default"
        onClick={(e) => {
          if (!track) return;
          const box = e.currentTarget.getBoundingClientRect();
          player.seekTo(track, (e.clientX - box.left) / box.width, queue);
        }}
      >
        <svg viewBox={`0 0 ${BARS} 100`} preserveAspectRatio="none" className="h-full w-full" aria-hidden>
          {bars.map((h, i) => (
            <rect
              key={i}
              x={i + 0.15}
              width={0.7}
              y={50 - h * 50}
              height={h * 100}
              rx={0.3}
              className={(i + 0.5) / BARS <= played ? "fill-primary" : "fill-muted-foreground/35"}
            />
          ))}
        </svg>
      </button>
      {markers.map((m) => (
        <span
          key={m.key}
          title={m.title}
          className="absolute -bottom-2.5 -translate-x-1/2"
          style={{ left: `${Math.min(Math.max(m.at, 0), 1) * 100}%` }}
        >
          {m.node}
        </span>
      ))}
    </div>
  );
}
